export interface MusixXmlSheet {
    transpose?: number;
    label?: string;
    info?: string;
    key: string;
    icon?: HTMLCanvasElement;
    xmlString: string;
}

export interface MusicXmlRendererDrawOptions {
    createPdf?: boolean;
    refWidth?: number;
    sheets: MusixXmlSheet[];
    abortable?: boolean;
    onProgress?: (e: { loaded: number; total: number }) => void;
}

export interface MusicXmlRendererSvgOptions {
    refWidth?: number;
    createPdf?: boolean;
    onProgress?: (e: { loaded: number; total: number }) => void;
}

export interface MusicXmlHtmlPage {
    element: HTMLElement;
    width: number;
    height: number;
}

export class MusicXmlRenderer {

    private static _uid: number = 1;

    private _lookup: { [key: string]: SVGSVGElement[] } = {};
    private _uidDraw: string = "";

    constructor() {
    }

    public async draw(options: MusicXmlRendererDrawOptions): Promise<{
        pdf?: jspdf.jsPDF;
        htmlPages: MusicXmlHtmlPage[];
    } | null> {

        options = e5.object.merge({
            createPdf: false,
            abortable: true,
            refWidth: 1024
        }, options);

        const uid = "uid-" + (MusicXmlRenderer._uid++);
        this._uidDraw = uid;

        let loaded = 0;
        let total = options.sheets.length;// * 2;

        const renderedSheets: {
            svgs: SVGSVGElement[],
            sheet: MusixXmlSheet,
        }[] = [];
        for (let sheet of options.sheets) {
            renderedSheets.push(
                {
                    svgs: await this.getOsmdSvgs(options.refWidth, sheet),
                    sheet: sheet
                });
            loaded++;
            if (typeof options.onProgress === "function") {
                options.onProgress(
                    {
                        loaded: loaded,
                        total: total
                    });
            }
            await e5.wait(50);

            if (options.abortable
                && this._uidDraw != uid) {
                return null;
            }
        }

        return await this.drawSvgs(
            renderedSheets,
            {
                refWidth: options.refWidth,
                createPdf: options.createPdf
            }
        );
    }

    private async getOsmdSvgs(refWidth: number, options: MusixXmlSheet): Promise<SVGSVGElement[]> {

        if (this._lookup[options.key]) {
            return this._lookup[options.key];
        }

        const xmlString = options.xmlString;

        var element = document.createElement("div");
        element.style.backgroundColor = "#FFF";
        element.style.width = refWidth + "px";
        document.body.appendChild(element);

        var osmd = new opensheetmusicdisplay.OpenSheetMusicDisplay(element, {
            autoResize: false,
            // pageFormat: "A4_P",
            backend: "svg",
            drawTitle: false,
            drawTimeSignatures: false,
            // renderSingleHorizontalStaffline: true,
            drawPartNames: false,
            drawMeasureNumbers: false
        });

        let pageWidth = 210;
        let pageHeight = 297 * 0.5;//So that a new svg is created for each "line"
        osmd.setCustomPageFormat(pageWidth, pageHeight);

        osmd.EngravingRules.StaffLineColor = "#666";
        osmd.EngravingRules.StaffLineWidth = 0.05;
        osmd.EngravingRules.MetronomeMarkYShift = -3;
        osmd.EngravingRules.MinimumStaffLineDistance = 4;
        osmd.EngravingRules.MinSkyBottomDistBetweenStaves = 5;
        // osmd.EngravingRules.LabelMarginBorderFactor = 2;
        osmd.EngravingRules.StaffHeight = 10;
        // osmd.EngravingRules.MaxPageToDrawNumber

        try {
            await osmd.load(xmlString);
        } catch (error) {
            console.error(error);
            console.log("Error with " + options.key);
            return [];
        }


        if (options.transpose) {
            /**
             * @see https://opensheetmusicdisplay.org/blog/music-xml-transposition-plugin-released/
             */
            const transposeValue = options.transpose;
            osmd.TransposeCalculator = new opensheetmusicdisplay.TransposeCalculator();
            osmd.Sheet.Transpose = transposeValue;
            osmd.updateGraphic();
        }

        osmd.render();

        element.remove();

        const svgs = this.collectSvgs([osmd], options);

        osmd.clear();

        //Caching disabled ... TODO: ....
        // this._lookup[options.key] = svgs;

        return svgs;
    }

    private collectSvgs(
        osmds: opensheetmusicdisplay.OpenSheetMusicDisplay[],
        options: MusixXmlSheet
    ): SVGSVGElement[] {

        const res: SVGSVGElement[] = [];

        for (const osmd of osmds) {

            for (const backend of osmd.Drawer.Backends) {
                const svg: SVGSVGElement = backend.getContext().svg;

                const clone = svg.cloneNode(true) as SVGSVGElement;

                document.body.appendChild(clone);
                const box = clone.getBBox();
                clone.remove();

                clone.setAttribute("viewBox", `${box.x} ${box.y} ${Math.ceil(box.width)} ${Math.ceil(box.height)}`);
                clone.setAttribute("width", Math.ceil(box.width) + "");
                clone.setAttribute("height", Math.ceil(box.height) + "");

                clone.querySelectorAll<SVGTextElement>("[font-family]")
                    .forEach((e) => {
                        /**
                         * @see https://github.com/parallax/jsPDF/blob/2d9a91916471f1fbe465dbcdc05db0cf22d720ec/src/jspdf.js
                         * @see https://www.devlinpeck.com/content/jspdf-custom-font
                         */
                        const supportedFonts = [
                            "courier",
                            "courier-bold",
                            "courier-boldoblique",
                            "courier-oblique",
                            "helvetica",
                            "helvetica-bold",
                            "helvetica-boldoblique",
                            "helvetica-oblique",
                            "symbol",
                            "times-roman",
                            "times-bold",
                            "time-italic",
                            "time-bolditalic",
                        ].concat([
                            "times",
                            "times new roman"
                        ]);
                        let fontFamily = e.getAttribute("font-family");
                        if (!supportedFonts.includes(fontFamily.toLowerCase())) {
                            console.warn(`Unsupported font-family "${fontFamily}". Fallback to "Times New Roman".`);
                            e.setAttribute("font-family", "Times New Roman");
                        }
                    });

                clone.querySelectorAll<SVGTextElement>("[font-size]")
                    .forEach((e) => {
                        let fontSize = e.getAttribute("font-size");
                        if (fontSize.trim().endsWith("pt")) {
                            const value = parseFloat(fontSize.trim()) * (1 / 0.75);//Pt to Pixel
                            e.setAttribute("font-size", value + "px");
                        }
                    });

                let offsetY = backend.graphicalMusicPage.MusicSystems[0].StaffLines[0].PositionAndShape.AbsolutePosition.y;
                offsetY = osmd.Drawer.calculatePixelDistance(offsetY);
                offsetY -= box.y;
                clone.setAttribute("data-offset-y", offsetY + "");

                res.push(clone);
            }
        }

        return res;
    }

    private async drawSvgs(items: {
        svgs: SVGSVGElement[],
        sheet: MusixXmlSheet,
    }[], options: MusicXmlRendererSvgOptions): Promise<{
        pdf?: jspdf.jsPDF;
        htmlPages: MusicXmlHtmlPage[];
    }> {
        options = e5.object.merge({
            createPdf: false,
            refWidth: 1024
        }, options);

        const refWidth: number = options.refWidth;

        let pageWidth = 210;
        let pageHeight = 297;

        const pixelToPage = pageWidth / refWidth;
        const pageToPixel = refWidth / pageWidth;
        const pointToPixel = pageToPixel / (1.333) * 0.5;

        const gapH = 0;
        const gapV = 7;

        const paddingH = 7;
        const paddingV = 7;

        const marginTop = 20;

        interface Row {
            items: {
                svg: SVGSVGElement;
                sheet: MusixXmlSheet;
                partIndex: number;
                width: number;
                height: number;
            }[];
            height: number;
            minY: number;
            maxY: number;
        }

        let rows: Row[] = [];
        let row: Row = null;
        let x = paddingH;

        for (let item of items) {

            let partIndex = 0;
            for (const svg of item.svgs) {

                const w = parseFloat(svg.getAttribute("width")) * pixelToPage;
                const h = parseFloat(svg.getAttribute("height")) * pixelToPage;
                const offsetY = parseFloat(svg.getAttribute("data-offset-y")) * pixelToPage;

                if (row
                    && row.items.length > 0
                    && (x + gapH + w) > pageWidth - (paddingH * 2)) {
                    row = null;
                }

                if (!row) {
                    x = 0;
                    row = {
                        items: [],
                        height: 0,
                        minY: Number.MAX_VALUE,
                        maxY: 0
                    };
                    rows.push(row);
                }

                row.minY = Math.min(row.minY, -offsetY);
                row.maxY = Math.max(row.maxY, -offsetY + h);
                row.height = row.maxY - row.minY;
                row.items.push({
                    sheet: item.sheet,
                    svg: svg,
                    partIndex: partIndex,
                    width: w,
                    height: h,
                })

                if (row.items.length > 1) {
                    x += gapH;
                }
                x += w;

                partIndex++;
            }
        }

        let pdf: jspdf.jsPDF;
        if (options.createPdf) {
            pdf = new jspdf.jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: [pageWidth, pageHeight]
            });
        }

        const htmlPages: MusicXmlHtmlPage[] = [];

        const addHtmlPage = () => {
            let p = document.createElement("div");
            p.style.width = (pageWidth * pageToPixel) + "px";
            p.style.height = (pageHeight * pageToPixel) + "px";

            htmlPages.push({
                element: p,
                width: (pageWidth * pageToPixel),
                height: (pageHeight * pageToPixel)
            });
            return p;
        }

        let loaded = 0;
        let htmlPage = addHtmlPage();
        x = paddingH;
        let y = paddingV + marginTop;
        for (let row of rows) {
            if (y + row.height > pageHeight - (paddingH * 2)) {
                htmlPage = addHtmlPage();
                if (pdf) {
                    pdf.addPage();
                }
                y = paddingV + marginTop;
            }

            let x = paddingH;
            let indexInRow = 0;
            for (let item of row.items) {

                const svg = item.svg;

                if (indexInRow > 0) {
                    svg.querySelectorAll(".vf-clef").forEach(e => e.remove());
                    // svg.querySelectorAll(".vf-clef").forEach(e => e.remove());


                    const groupStuff = svg.querySelector(":scope > path + path + rect:last-child");
                    if (groupStuff) {
                        groupStuff.previousSibling.previousSibling.remove();
                        groupStuff.previousSibling.remove();
                        groupStuff.remove();
                    }

                    // const lastChild = svg.children[svg.children.length - 1];
                    // if(lastChild 
                    //     && lastChild.tagName.toLowerCase() === "rect"
                    //     && lastChild.previousSibling && (lastChild.previousSibling as HTMLElement).tagName.toLowerCase() === "path"){

                    //     if(){
                    //         lastChild.previousSibling.remove();
                    //     }
                    // }

                }

                /**
                 * Fillup staff lines to right of the page.
                 */
                if (indexInRow === row.items.length - 1) {
                    let offset = (pageWidth - paddingH) - (x + item.width);
                    offset *= pageToPixel;
                    let w = parseFloat(svg.getAttribute("width")) + offset;
                    svg.setAttribute("width", w + "");
                    let vb = svg.getAttribute("viewBox");
                    let vbs = vb.split(" ");
                    vbs[2] = w + "";
                    svg.setAttribute("viewBox", vbs.join(" "));
                    svg.querySelectorAll<SVGPathElement>(":scope > path").forEach((e, key) => {
                        if (e.getAttribute("stroke") != "#666") {
                            return;
                        }
                        let d = e.getAttribute("d");
                        d += ` h${offset}`;
                        e.setAttribute("d", d);
                    });

                    // svg.querySelectorAll(":scope > rect").forEach(e => e.remove());
                }



                const w = parseFloat(svg.getAttribute("width")) * pixelToPage;
                const h = parseFloat(svg.getAttribute("height")) * pixelToPage;
                const offsetY = parseFloat(svg.getAttribute("data-offset-y")) * pixelToPage;

                const ty = y - offsetY - row.minY;
                const tx = x;

                let iconWidth = 7;
                let iconHeight = 0;
                let iconX = x;
                let iconY = 0;

                if (item.sheet.icon) {
                    iconHeight = (item.sheet.icon.height / item.sheet.icon.width) * iconWidth;
                    iconY = y - row.minY - iconHeight - 4;
                }

                svg.style.position = "absolute";
                svg.style.left = `${tx * pageToPixel}px`;
                svg.style.top = `${ty * pageToPixel}px`;
                htmlPage.appendChild(svg);


                if (pdf) {
                    await pdf.svg(svg, {
                        x: tx,
                        y: ty,
                        width: w,
                        height: h,
                    });
                }

                if (item.sheet.icon
                    && item.partIndex === 0) {

                    let can = document.createElement("canvas");
                    can.width = iconWidth * pageToPixel;
                    can.height = iconHeight * pageToPixel;
                    let ctx = can.getContext("2d");
                    ctx.drawImage(item.sheet.icon, 0, 0, can.width, can.height);

                    /**
                     * Add it as background so that cloning the HTML-Node works :)
                     */
                    can.style.backgroundImage = `url(${can.toDataURL()})`;
                    can.style.position = "absolute";
                    can.style.left = `${iconX * pageToPixel}px`;
                    can.style.top = `${iconY * pageToPixel}px`;
                    htmlPage.appendChild(can);

                    if (pdf) {
                        await pdf.addImage({
                            // imageData: item.sheet.icon.toDataURL("image/png"),
                            imageData: item.sheet.icon,
                            x: iconX,
                            y: iconY,
                            width: iconWidth,
                            height: iconHeight
                        });
                    }
                }

                if (item.sheet.label
                    && item.partIndex === 0) {

                    const labelX = x;
                    const labelY = iconY - 5;

                    const fontFamily = 'helvetica';
                    const fontSize = 10;

                    let label = document.createElement("div");
                    label.style.position = "absolute";
                    label.textContent = item.sheet.label;
                    label.style.fontFamily = fontFamily;
                    label.style.fontSize = `${fontSize * pointToPixel}px`;
                    label.style.left = `${labelX * pageToPixel}px`;
                    label.style.top = `${labelY * pageToPixel}px`;
                    label.style.transform = "translateY(-100%)";
                    htmlPage.appendChild(label);

                    if (pdf) {
                        pdf.setFontSize(fontSize);
                        pdf.setFont(fontFamily);
                        pdf.text(String(item.sheet.label || ""), labelX, labelY, { baseline: "bottom" });
                    }
                }

                if (item.sheet.info
                    && item.partIndex === 0) {

                    const infoX = x;
                    const infoY = iconY - 1.25;

                    const fontFamily = 'helvetica';
                    const fontSize = 8;

                    let label = document.createElement("div");
                    label.style.position = "absolute";
                    label.textContent = item.sheet.info;
                    label.style.fontFamily = fontFamily;
                    label.style.fontSize = `${fontSize * pointToPixel}px`;
                    label.style.left = `${infoX * pageToPixel}px`;
                    label.style.top = `${infoY * pageToPixel}px`;
                    label.style.transform = "translateY(-100%)";
                    htmlPage.appendChild(label);

                    if (pdf) {
                        pdf.setFontSize(fontSize);
                        pdf.setFont(fontFamily);
                        pdf.text(String(item.sheet.info || ""), infoX, infoY, { baseline: "bottom" });
                    }
                }

                x += w + gapH;
                indexInRow++;

                loaded++;

                if (typeof options.onProgress === "function") {
                    options.onProgress(
                        {
                            loaded: loaded,
                            total: items.length
                        });
                }
            }

            y += row.height + gapV + marginTop;
        }

        return {
            pdf,
            htmlPages
        };
    }
}